import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Upload, FileText, Loader2, CheckCircle2, AlertTriangle, Eye, Sparkles, Plus } from 'lucide-react';

const ConfBadge = ({ item }) => {
  if (!item) return null;
  const c = item.confidence ?? 0;
  const origen = item.origen || 'no_especificado';
  const color = origen === 'no_especificado' ? 'bg-red-100 text-red-700'
    : origen === 'inferido' || c < 80 ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700';
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${color}`} title={item.fuente || ''}>
      {origen === 'no_especificado' ? 'No especificado' : `${origen} ${c}%`}
    </span>
  );
};

const Field = ({ label, item, onChange }) => (
  <div className="space-y-1">
    <div className="flex items-center gap-2">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <ConfBadge item={item} />
    </div>
    <Input value={item?.valor ?? ''} className="h-8 text-sm"
      onChange={e => onChange({ ...item, valor: e.target.value })} />
    {item?.fuente && (
      <p className="text-[10px] text-slate-400 italic truncate" title={item.fuente}>Fuente: "{item.fuente}"</p>
    )}
  </div>
);

export default function ProtocolImportPage() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [imports, setImports] = useState([]);
  const [current, setCurrent] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [progreso, setProgreso] = useState(null);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = () => api.get('/protocol-import').then(r => setImports(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const p = current?.propuesta;

  const setP = (updater) => setCurrent(prev => ({ ...prev, propuesta: typeof updater === 'function' ? updater(prev.propuesta) : updater }));

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/protocol-import/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(r.data.es_nueva_version ? `Nueva versión detectada (v${r.data.version})` : 'Protocolo cargado');
      setCurrent({ ...r.data, propuesta: null });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al cargar el protocolo');
    } finally { setUploading(false); }
  };

  const retryAnalysis = async () => {
    // v10.4.5: reintenta sobre la MISMA importación (no crea v2/v3)
    setAnalyzing(true);
    setProgreso(null);
    try {
      const r = await api.post(`/protocol-import/${current.id}/retry`);
      if (r.data?.async) {
        toast.info('Reintentando análisis (sin crear nueva versión)…', { duration: 6000 });
        const startedAt = Date.now();
        let cloudflareFailStreak = 0;
        while (Date.now() - startedAt < 360_000) {
          await new Promise((resolve) => setTimeout(resolve, 3000));
          let poll;
          try {
            poll = await api.get(`/protocol-import/${current.id}`);
          } catch (netErr) {
            const code = netErr.response?.status;
            const isTransient = !code || [502, 503, 504, 520, 521, 522, 523, 524].includes(code);
            if (isTransient && (cloudflareFailStreak += 1) <= 10) {
              setProgreso((prev) => ({ ...(prev || {}), red_intermitente: true, reintentos: cloudflareFailStreak }));
              continue;
            }
            throw netErr;
          }
          cloudflareFailStreak = 0;
          const d = poll.data;
          if (d.analisis_progreso) setProgreso({ ...d.analisis_progreso, red_intermitente: false });
          if ((d.status === 'analizado' || d.status === 'analizado_parcial') && d.propuesta) {
            setCurrent(d); setProgreso(null);
            toast.success(d.status === 'analizado_parcial' ? 'Análisis parcial recuperado. Revisa.' : 'Protocolo analizado.');
            load();
            return;
          }
          if (d.status === 'error' || d.status === 'timeout') {
            setCurrent(d); setProgreso(null);
            toast.error(d.last_analysis_error || 'Reintento falló', { duration: 15000 });
            load();
            return;
          }
        }
        throw new Error('El reintento excedió 6 min. Vuelve a intentar.');
      }
    } catch (e) {
      let msg = e.response?.data?.detail || e.message || 'Error al reintentar';
      if (typeof msg === 'string' && /<html|<!doctype/i.test(msg)) {
        msg = 'Servidor no disponible temporalmente. Reintenta en unos segundos.';
      }
      toast.error(msg, { duration: 12000 });
      setProgreso(null);
    } finally { setAnalyzing(false); }
  };

  const analyze = async () => {
    setAnalyzing(true);
    setProgreso(null);
    try {
      const r = await api.post(`/protocol-import/${current.id}/analyze`);
      // Backend now runs analysis in background (returns 202-style response
      // with status="analizando"). Poll the import until status changes.
      if (r.data?.async) {
        toast.info('Analizando el protocolo con IA...', { duration: 8000 });
        const startedAt = Date.now();
        let lastPolledStatus = null;
        let cloudflareFailStreak = 0;
        // v10.4.2: partido en 5 sub-llamadas → cada una ~30-60s → total ~5min máx
        while (Date.now() - startedAt < 360_000) { // 6-minute cap
          await new Promise((resolve) => setTimeout(resolve, 3000));
          let poll;
          try {
            poll = await api.get(`/protocol-import/${current.id}`);
          } catch (netErr) {
            // v10.4.2: Cloudflare 520/502/503 durante el polling = pod
            // saturado momentáneamente. Ignoramos y reintentamos hasta
            // 10 veces seguidas antes de rendirnos.
            const code = netErr.response?.status;
            const isTransient = !code || [502, 503, 504, 520, 521, 522, 523, 524].includes(code);
            if (isTransient) {
              cloudflareFailStreak += 1;
              if (cloudflareFailStreak <= 10) {
                setProgreso((prev) => ({
                  ...(prev || {}), red_intermitente: true,
                  reintentos: cloudflareFailStreak,
                }));
                continue;
              }
            }
            throw netErr;
          }
          cloudflareFailStreak = 0;
          const d = poll.data;
          lastPolledStatus = d.status;
          if (d.analisis_progreso) setProgreso({ ...d.analisis_progreso, red_intermitente: false });
          if ((d.status === 'analizado' || d.status === 'analizado_parcial') && d.propuesta) {
            setCurrent(d);
            setProgreso(null);
            if (d.status === 'analizado_parcial') {
              toast.warning(
                `Protocolo analizado parcialmente. Algunas secciones fallaron: ${
                  (d.analisis_progreso?.fases_con_error || []).join(', ')
                }. Revisa la propuesta.`,
                { duration: 15000 }
              );
            } else {
              toast.success('Protocolo analizado. Revisa la propuesta antes de crear el escenario.');
            }
            return;
          }
          if (d.status === 'error' || d.status === 'timeout') {
            const detail = d.last_analysis_error || 'El análisis falló en el servidor';
            const snip = d.last_analysis_raw_snippet;
            setCurrent(d);
            setProgreso(null);
            toast.error(detail, {
              duration: 15000,
              description: (d.error_code ? `[${d.error_code}] ` : '') + (snip ? `Fragmento: ${String(snip).slice(0, 200)}` : ''),
            });
            throw new Error(detail);
          }
        }
        throw new Error(
          `El análisis excedió 6 min sin respuesta (último status: ${lastPolledStatus || 'desconocido'}). ` +
          'Puede ser saldo de IA agotado o proveedor saturado. Reintenta.'
        );
      }
      // Legacy synchronous response fallback
      setCurrent((prev) => ({ ...prev, propuesta: r.data.propuesta, status: 'analizado' }));
      toast.success('Protocolo analizado. Revisa la propuesta antes de crear el escenario.');
    } catch (e) {
      // v10.4.2: NUNCA mostrar HTML crudo en toast. Sanitizar.
      let msg = e.response?.data?.detail || e.message || 'Error en el análisis';
      if (typeof msg === 'string' && /<html|<!doctype/i.test(msg)) {
        msg = 'Servidor no disponible temporalmente. Reintenta en unos segundos.';
      }
      toast.error(msg, { duration: 12000 });
      setProgreso(null);
    } finally { setAnalyzing(false); }
  };

  const saveProposal = async () => {
    setSaving(true);
    try {
      await api.put(`/protocol-import/${current.id}/proposal`, { propuesta: p });
      toast.success('Propuesta guardada');
    } catch { toast.error('Error al guardar'); } finally { setSaving(false); }
  };

  const createScenario = async () => {
    setCreating(true);
    try {
      await api.put(`/protocol-import/${current.id}/proposal`, { propuesta: p });
      const r = await api.post(`/protocol-import/${current.id}/create-scenario`, {});
      toast.success('Escenario creado en estado BORRADOR');
      navigate(`/app/admin/scenarios/${r.data.scenario_id}/edit`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al crear el escenario');
    } finally { setCreating(false); }
  };

  const openImport = async (id) => {
    const r = await api.get(`/protocol-import/${id}`);
    setCurrent(r.data);
  };

  const est = p?.estado_inicial || {};
  const pac = p?.paciente || {};

  return (
    <div className="space-y-6 p-1" data-testid="protocol-import-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Crear desde protocolo</h1>
          <p className="text-sm text-slate-500">Carga un protocolo clínico y SimuClinic analizará su contenido para generar una propuesta de escenario y evaluación.</p>
        </div>
        <Button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="upload-protocol-btn">
          {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
          Subir protocolo (.docx)
        </Button>
        <input ref={fileRef} type="file" accept=".docx" className="hidden"
          onChange={e => upload(e.target.files?.[0])} data-testid="protocol-file-input" />
      </div>

      {!current && (
        <Card>
          <CardHeader><CardTitle className="text-base">Protocolos cargados</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {imports.length === 0 && <p className="text-sm text-slate-400">Aún no hay protocolos. Sube un .docx para comenzar.</p>}
            {imports.map(i => (
              <button key={i.id} onClick={() => openImport(i.id)}
                className="w-full flex items-center gap-3 border rounded-lg p-3 hover:bg-slate-50 text-left"
                data-testid={`import-row-${i.id}`}>
                <FileText className="w-4 h-4 text-cyan-600 shrink-0" />
                <span className="text-sm font-medium flex-1 truncate">{i.filename}</span>
                <Badge variant="outline" className="text-[10px]">v{i.version}</Badge>
                <Badge className={`text-[10px] ${
                  i.status === 'escenario_creado' ? 'bg-emerald-100 text-emerald-700' :
                  i.status === 'analizado' ? 'bg-cyan-100 text-cyan-700' :
                  i.status === 'analizado_parcial' ? 'bg-amber-100 text-amber-700' :
                  (i.status === 'error' || i.status === 'timeout') ? 'bg-red-100 text-red-700' :
                  'bg-slate-100 text-slate-600'
                }`}>
                  {i.status.replace('_', ' ')}
                </Badge>
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {current && (
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base flex items-center gap-2">
                <FileText className="w-4 h-4" /> {current.filename} <Badge variant="outline">v{current.version}</Badge>
              </CardTitle>
              <CardDescription>Hash: {current.hash?.slice(0, 16)}… · Estado: {current.status}</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrent(null)}>Volver</Button>
              {(current.status === 'error' || current.status === 'timeout' || current.status === 'analizado_parcial') && (
                <Button size="sm" variant="outline" onClick={retryAnalysis} disabled={analyzing}
                  data-testid="retry-analysis-btn" className="border-amber-500 text-amber-700 hover:bg-amber-50">
                  {analyzing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                  Reintentar análisis
                </Button>
              )}
              <Button size="sm" onClick={analyze} disabled={analyzing} data-testid="analyze-protocol-btn">
                {analyzing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />}
                {p ? 'Reanalizar protocolo' : 'Analizar documento'}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {(current.status === 'error' || current.status === 'timeout') && current.last_analysis_error && !analyzing && (
              <div className="mb-4 p-3 border border-red-300 bg-red-50 rounded-lg" data-testid="error-detail">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-red-800">
                      No fue posible analizar el protocolo
                      {current.error_code && <span className="ml-2 text-xs font-mono bg-red-100 px-1.5 py-0.5 rounded">{current.error_code}</span>}
                    </p>
                    <p className="text-xs text-red-700 mt-1">{current.last_analysis_error}</p>
                  </div>
                </div>
              </div>
            )}
            {current.status === 'analizado_parcial' && !analyzing && (
              <div className="mb-4 p-3 border border-amber-300 bg-amber-50 rounded-lg" data-testid="partial-detail">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-800">Análisis parcial — revisa antes de crear el escenario</p>
                    {current.last_analysis_error && <p className="text-xs text-amber-700 mt-1">{current.last_analysis_error}</p>}
                  </div>
                </div>
              </div>
            )}
            {analyzing && (
              <div className="py-6 space-y-3" data-testid="analyze-progress">
                <p className="text-sm text-slate-600 text-center">
                  Analizando el protocolo con IA (5 fases)…
                </p>
                {progreso && (
                  <div className="space-y-2 max-w-md mx-auto">
                    <div className="flex justify-between text-xs text-slate-600">
                      <span>Fase {progreso.indice || 0} de {progreso.total || 5}: <b className="capitalize">{progreso.fase_actual || '—'}</b></span>
                      <span>{Math.round(((progreso.indice || 0) / (progreso.total || 5)) * 100)}%</span>
                    </div>
                    <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-cyan-500 transition-all duration-500"
                        style={{ width: `${((progreso.indice || 0) / (progreso.total || 5)) * 100}%` }}
                      />
                    </div>
                    {progreso.descripcion && (
                      <p className="text-[11px] text-slate-500 italic text-center">{progreso.descripcion}</p>
                    )}
                    {progreso.red_intermitente && (
                      <p className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 text-center">
                        ⚠ Servidor saturado momentáneamente. Reintento {progreso.reintentos}/10…
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            {!p && !analyzing && (
              <div className="text-sm text-slate-500 py-6 text-center">Presiona "Analizar documento" para extraer la información clínica.</div>
            )}
            {p && !analyzing && (
              <Tabs defaultValue="paciente">
                <TabsList className="flex-wrap h-auto">
                  {['paciente', 'vitales', 'acciones', 'medicamentos', 'eventos', 'evaluacion', 'pendientes'].map(t => (
                    <TabsTrigger key={t} value={t} className="text-xs capitalize" data-testid={`tab-${t}`}>{t === 'vitales' ? 'Signos vitales y ECG' : t}</TabsTrigger>
                  ))}
                </TabsList>

                <TabsContent value="paciente" className="grid grid-cols-2 md:grid-cols-3 gap-4 pt-4">
                  {['sexo', 'edad', 'iniciales', 'antecedentes', 'motivo_consulta', 'tiempo_evolucion'].map(k => (
                    <Field key={k} label={k.replace('_', ' ')} item={pac[k]}
                      onChange={v => setP(prev => ({ ...prev, paciente: { ...prev.paciente, [k]: v } }))} />
                  ))}
                </TabsContent>

                <TabsContent value="vitales" className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4">
                  {['fc', 'pa_sistolica', 'pa_diastolica', 'spo2', 'fr', 'temperatura', 'glasgow', 'conciencia', 'ritmo_ecg', 'qrs_type'].map(k => (
                    <Field key={k} label={k} item={est[k]}
                      onChange={v => setP(prev => ({ ...prev, estado_inicial: { ...prev.estado_inicial, [k]: v } }))} />
                  ))}
                </TabsContent>

                <TabsContent value="acciones" className="space-y-2 pt-4">
                  {(p.acciones || []).map((a, i) => (
                    <div key={i} className="border rounded-lg p-2 flex items-center gap-2">
                      <Input value={a.nombre || ''} className="h-8 text-sm flex-1"
                        onChange={e => setP(prev => { const acc = [...prev.acciones]; acc[i] = { ...acc[i], nombre: e.target.value }; return { ...prev, acciones: acc }; })} />
                      <label className="text-xs flex items-center gap-1">
                        <input type="checkbox" checked={!!a.obligatoria}
                          onChange={e => setP(prev => { const acc = [...prev.acciones]; acc[i] = { ...acc[i], obligatoria: e.target.checked }; return { ...prev, acciones: acc }; })} />
                        Obligatoria
                      </label>
                      <ConfBadge item={a} />
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="medicamentos" className="space-y-2 pt-4">
                  {(p.medicamentos || []).map((m, i) => (
                    <div key={i} className="border rounded-lg p-3 space-y-1" data-testid={`med-row-${i}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold">{m.nombre}</span>
                        <span className="text-xs text-slate-500">{m.dosis} {m.unidad} · {m.via || 'vía no especificada'} · {m.modo || ''}</span>
                        <ConfBadge item={m} />
                        {m.match_status === 'encontrado'
                          ? <Badge className="bg-emerald-100 text-emerald-700 text-[10px]"><CheckCircle2 className="w-3 h-3 mr-1" />{m.medication_match_nombre}</Badge>
                          : <Badge className="bg-red-100 text-red-700 text-[10px]"><AlertTriangle className="w-3 h-3 mr-1" />Medicamento no encontrado</Badge>}
                        {m.match_status === 'encontrado' && !m.presentacion_disponible &&
                          <Badge className="bg-amber-100 text-amber-700 text-[10px]">Sin presentación compatible</Badge>}
                      </div>
                      {m.lavado && <p className="text-xs text-slate-500">Lavado: {m.lavado}</p>}
                      {m.fuente && <p className="text-[10px] text-slate-400 italic">Fuente: "{m.fuente}"</p>}
                      {m.match_status !== 'encontrado' && (
                        <div className="flex items-center gap-2">
                          <Button size="sm" variant="outline" className="h-7 text-xs"
                            onClick={async () => {
                              try {
                                const r = await api.post(`/protocol-import/${current.id}/create-medication`, { med_index: i });
                                toast.success(r.data.message);
                                setP(prev => { const md = [...prev.medicamentos]; md[i] = { ...md[i], match_status: 'encontrado', medication_id: r.data.medication_id, medication_match_nombre: r.data.nombre, decision: 'creado' }; return { ...prev, medicamentos: md }; });
                              } catch (e) { toast.error(e.response?.data?.detail || 'Error al crear medicamento'); }
                            }} data-testid={`create-med-btn-${i}`}>
                            <Plus className="w-3 h-3 mr-1" /> Crear nuevo medicamento
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500"
                            onClick={() => setP(prev => { const md = [...prev.medicamentos]; md[i] = { ...md[i], decision: md[i].decision === 'ignorar' ? '' : 'ignorar' }; return { ...prev, medicamentos: md }; })}
                            data-testid={`med-decision-${i}`}>
                            {m.decision === 'ignorar' ? 'Ignorado ✓ (click para revertir)' : 'Ignorar'}
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="eventos" className="space-y-2 pt-4">
                  {(p.eventos || []).map((ev, i) => (
                    <div key={i} className="border rounded-lg p-3 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">{ev.nombre}</span>
                        <Badge variant="outline" className="text-[10px]">trigger: {ev.trigger_tipo}{ev.trigger_medicamento ? ` · ${ev.trigger_medicamento}` : ''}</Badge>
                        <ConfBadge item={ev} />
                      </div>
                      <p className="text-xs text-slate-600">{ev.descripcion}</p>
                      {ev.estado_resultante && (
                        <p className="text-xs text-cyan-700">
                          → {Object.entries(ev.estado_resultante).filter(([, v]) => v != null).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                        </p>
                      )}
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="evaluacion" className="space-y-2 pt-4">
                  {(p.criterios_evaluacion || []).map((c, i) => (
                    <div key={i} className="border rounded-lg p-2 flex items-center gap-2 flex-wrap" data-testid={`criterio-row-${i}`}>
                      <Badge variant="outline" className="text-[10px]">{c.codigo || `C${i + 1}`}</Badge>
                      <Badge className="bg-slate-100 text-slate-600 text-[10px]">{c.categoria}</Badge>
                      <span className="text-sm flex-1">{c.accion_esperada}</span>
                      {c.obligatorio && <Badge className="bg-cyan-100 text-cyan-700 text-[10px]">Obligatorio</Badge>}
                      <Input type="number" placeholder="pts" value={c.puntaje ?? ''} className="h-7 w-16 text-xs"
                        onChange={e => setP(prev => { const cr = [...prev.criterios_evaluacion]; cr[i] = { ...cr[i], puntaje: e.target.value ? parseInt(e.target.value) : null }; return { ...prev, criterios_evaluacion: cr }; })} />
                      {c.puntaje == null && <Badge className="bg-amber-100 text-amber-700 text-[10px]">Ponderación pendiente</Badge>}
                      <ConfBadge item={c} />
                    </div>
                  ))}
                </TabsContent>

                <TabsContent value="pendientes" className="space-y-2 pt-4">
                  {(p.campos_pendientes || []).map((c, i) => (
                    <p key={i} className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">⚠ {c}</p>
                  ))}
                  {(p.secciones_no_interpretadas || []).map((s, i) => (
                    <div key={i} className="text-xs bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                      <p className="font-semibold text-red-700">No fue posible interpretar esta sección:</p>
                      <p className="text-slate-600 italic">"{s.texto}"</p>
                    </div>
                  ))}
                  {!(p.campos_pendientes || []).length && !(p.secciones_no_interpretadas || []).length &&
                    <p className="text-sm text-emerald-600">Sin campos pendientes.</p>}
                </TabsContent>
              </Tabs>
            )}
            {p && !analyzing && (
              <div className="flex gap-2 justify-end pt-4 border-t mt-4">
                <Button variant="outline" onClick={saveProposal} disabled={saving} data-testid="save-proposal-btn">
                  {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Guardar propuesta
                </Button>
                <Button onClick={createScenario} disabled={creating} data-testid="create-scenario-from-protocol-btn">
                  {creating ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Eye className="w-4 h-4 mr-1" />}
                  Crear escenario desde protocolo (borrador)
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
